/**
 * AI先輩が答えない領域（issue #109・#465・docs/01 11-8）。
 *
 * ここでできること:
 *  - いまの領域の一覧（領域・拾う言葉・お返しする案内・連絡先・件数だけ数えるか）
 *  - 領域を**足す**・**直す**・**外す**（受け持ちが全社の方だけ。自拠点を受け持つ方は見るだけ）
 *  - **既定の内容に戻す**（#472 の決定。領域ごとと、画面全体）。既定の拾う言葉のうち、いまの一覧に
 *    無いものだけを足し戻します。足した言葉・直した名前・案内・連絡先・「件数だけ数える」は変えません。
 *    外した既定の領域は、既定の内容で作り直します（外す前に足していた言葉は戻りません）。
 *    押す前に確認の窓で戻る言葉を見せ、戻すものが無いときは押せないようにして理由を出します
 *
 * 書き換える画面は**ここ1か所**です（「会社の設定」の画面には出しません）。同じ値を2か所から
 * 書き換えられると、どちらが効いているのか分からなくなるためです。
 *
 * 決定22 に沿って、保存は明示のボタンで行い、終わったら必ず結果を出します。外すときは確認の窓を
 * はさみます。足す・直す・外す・既定の内容に戻すは、操作履歴に「設定を変えた」として残ります
 * （サーバ側で記録）。
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ApiError, messageForStatus } from '../api/client.js';
import { useApi } from '../api/context.js';
import type {
  AiRemovedDefaultTopic,
  AiRestoreDefaultsResponse,
  AiRestrictedTopic,
  AiRestrictedTopicsResponse,
} from '../api/types.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { loadFailure, type LoadFailure } from '../api/load-failure.js';
import { Button } from '../components/ui/Button.js';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.js';
import { DataTable } from '../components/ui/DataTable.js';
import { FormCard } from '../components/ui/FormCard.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { ResultBanner } from '../components/ui/ResultBanner.js';
import { splitKeywords } from '../ai/restricted-topics.js';
import { endsWithRequest, withReason } from '../format/sentence.js';
import { ROLE_REQUIREMENTS, useRoleWording } from '../auth/role-wording.js';

/** 入力の長さの上限（サーバの TOPIC_LIMITS と同じ）。 */
const LABEL_MAX = 40;
const GUIDANCE_MAX = 500;
const CONTACT_MAX = 200;

/** 確認の窓で、戻る言葉を先頭から何語まで見せるか（多いときは「ほか◯語」）。 */
const PREVIEW_WORDS = 10;

/** 「既定の内容に戻す」を押したときに何を戻すか。 */
type RestoreTarget = { kind: 'one'; topic: AiRestrictedTopic } | { kind: 'all' };

interface Banner {
  tone: 'success' | 'error';
  message: string;
  nextStep?: string;
}

/** 入力欄の中身。 */
interface Draft {
  label: string;
  /** 拾う言葉（「、」か改行で区切って入れる） */
  keywords: string;
  guidance: string;
  contact: string;
  sensitive: boolean;
}

const EMPTY_DRAFT: Draft = { label: '', keywords: '', guidance: '', contact: '', sensitive: true };

function draftOf(topic: AiRestrictedTopic): Draft {
  return {
    label: topic.label,
    keywords: topic.keywords.join('、'),
    guidance: topic.guidance,
    contact: topic.contact,
    sensitive: topic.sensitive,
  };
}

/** 送る前に、画面の中で分かる間違いを先にお伝えする。 */
function checkDraft(draft: Draft): string | null {
  const label = draft.label.trim();
  if (label === '') return '領域の名前を入れてください。';
  if ([...label].length > LABEL_MAX) return `領域の名前は${LABEL_MAX}文字までです。`;
  if (splitKeywords(draft.keywords).length === 0) return '拾う言葉を1つ以上入れてください。';
  const guidance = draft.guidance.trim();
  if (guidance === '') return 'お返しする案内を入れてください。';
  if ([...guidance].length > GUIDANCE_MAX) return `お返しする案内は${GUIDANCE_MAX}文字までです。`;
  if ([...draft.contact.trim()].length > CONTACT_MAX) return `連絡先は${CONTACT_MAX}文字までです。`;
  return null;
}

function bodyOf(draft: Draft): Record<string, unknown> {
  return {
    label: draft.label.trim(),
    keywords: splitKeywords(draft.keywords),
    guidance: draft.guidance.trim(),
    contact: draft.contact.trim(),
    sensitive: draft.sensitive,
  };
}

export function AiRestrictedTopicsPage(): ReactNode {
  const api = useApi();
  // 案内に出す役職の呼び名（その会社の呼び名・issue #480）
  const wording = useRoleWording();
  const [data, setData] = useState<AiRestrictedTopicsResponse | null>(null);
  const [error, setError] = useState<LoadFailure | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [result, setResult] = useState<Banner | null>(null);
  const [busy, setBusy] = useState(false);

  // 足す
  const [adding, setAdding] = useState<Draft>(EMPTY_DRAFT);
  // 直す（1件ずつ）
  const [editing, setEditing] = useState<AiRestrictedTopic | null>(null);
  const [editDraft, setEditDraft] = useState<Draft>(EMPTY_DRAFT);
  // 外すの確認
  const [removing, setRemoving] = useState<AiRestrictedTopic | null>(null);
  // 既定の内容に戻すの確認
  const [restoring, setRestoring] = useState<RestoreTarget | null>(null);

  const reload = useCallback(() => setReloadKey((n) => n + 1), []);

  /**
   * 「直す」を押したら、直す欄を画面に出し、最初の欄に合わせる。一覧が長いと、欄が一覧の
   * 下に出ても気づけないため（押したのに何も起きないように見えない・決定22）
   */
  const editArea = useRef<HTMLDivElement>(null);
  const editingKey = editing?.key ?? null;
  useEffect(() => {
    if (editingKey === null) return;
    const area = editArea.current;
    area?.scrollIntoView?.({ block: 'start' });
    area?.querySelector<HTMLInputElement>('input')?.focus({ preventScroll: true });
  }, [editingKey]);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api
      .get<AiRestrictedTopicsResponse>('/admin/ai/restricted-topics')
      .then((loaded) => {
        if (!cancelled) setData(loaded);
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(loadFailure(caught));
      });
    return () => {
      cancelled = true;
    };
  }, [api, reloadKey]);

  const canEdit = data?.can_edit === true;

  async function addTopic(): Promise<void> {
    const problem = checkDraft(adding);
    if (problem !== null) {
      setResult({ tone: 'error', message: problem });
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const saved = await api.post<AiRestrictedTopicsResponse>('/admin/ai/restricted-topics', {
        body: bodyOf(adding),
      });
      setData(saved);
      setResult({
        tone: 'success',
        message: `「${adding.label.trim()}」を足しました。次の質問から、この領域には答えず案内をお返しします。`,
      });
      setAdding(EMPTY_DRAFT);
    } catch (caught: unknown) {
      setResult(failure('足せませんでした', caught, '「足す」'));
    } finally {
      setBusy(false);
    }
  }

  function startEditing(topic: AiRestrictedTopic): void {
    setEditing(topic);
    setEditDraft(draftOf(topic));
    setResult(null);
  }

  async function saveEdit(): Promise<void> {
    if (!editing) return;
    const problem = checkDraft(editDraft);
    if (problem !== null) {
      setResult({ tone: 'error', message: problem });
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const saved = await api.patch<AiRestrictedTopicsResponse>(
        `/admin/ai/restricted-topics/${encodeURIComponent(editing.key)}`,
        { body: bodyOf(editDraft) },
      );
      setData(saved);
      setEditing(null);
      setResult({
        tone: 'success',
        message: `「${editDraft.label.trim()}」を保存しました。次の質問から、直した内容で案内します。`,
      });
    } catch (caught: unknown) {
      setResult(failure('保存できませんでした', caught, '「保存する」'));
      if (caught instanceof ApiError && caught.status === 404) reload();
    } finally {
      setBusy(false);
    }
  }

  async function removeTopic(topic: AiRestrictedTopic): Promise<void> {
    setBusy(true);
    setResult(null);
    try {
      const saved = await api.del<AiRestrictedTopicsResponse>(
        `/admin/ai/restricted-topics/${encodeURIComponent(topic.key)}`,
      );
      setData(saved);
      setRemoving(null);
      if (editing?.key === topic.key) setEditing(null);
      setResult({
        tone: 'success',
        message: `「${topic.label}」を外しました。次の質問から、この領域の質問にも AI先輩が答えます。`,
        // 既定の領域は「既定の内容に戻す」で作り直せる（#472）。足した領域は足し直すしかない
        nextStep:
          topic.missing_default_keywords !== null
            ? '元に戻すときは、上の「既定の内容に戻す」を押してください（外す前に足した言葉は戻りません）。'
            : '元に戻すときは、同じ内容で「領域を足す」から足してください。',
      });
    } catch (caught: unknown) {
      setRemoving(null);
      setResult({
        tone: 'error',
        message: withReason('外せませんでした', toMessage(caught)),
        nextStep: '時間をおいて、もう一度お試しください。',
      });
      if (caught instanceof ApiError && caught.status === 404) reload();
    } finally {
      setBusy(false);
    }
  }

  async function restoreDefaults(target: RestoreTarget): Promise<void> {
    setBusy(true);
    setResult(null);
    try {
      const saved = await api.post<AiRestoreDefaultsResponse>(
        '/admin/ai/restricted-topics/restore-defaults',
        { body: target.kind === 'one' ? { key: target.topic.key } : {} },
      );
      const { restored, ...view } = saved;
      setData(view);
      setRestoring(null);
      // 直している途中の領域に言葉が足されたら、欄が古いまま保存されないよう閉じる
      if (editing !== null && restored.some((r) => r.key === editing.key)) setEditing(null);
      setResult({
        tone: 'success',
        message: `${restored.map(restoredSentence).join('')}次の質問から効きます。`,
      });
    } catch (caught: unknown) {
      setRestoring(null);
      setResult({
        tone: 'error',
        message: withReason('戻せませんでした', toMessage(caught)),
        nextStep: '画面を読み直しました。いまの内容をご確認ください。',
      });
      reload();
    } finally {
      setBusy(false);
    }
  }

  // 「既定の内容に戻す」は書き換えられる方だけに出す
  const plan = data && canEdit ? restorePlan(data) : null;

  return (
    <article>
      <PageHeader
        title="答えない領域"
        description="この領域の質問には、AI先輩は答えず、決められた案内文をお返しします。"
      />
      {data ? (
        <p className="admin-note">
          {canEdit
            ? '足す・直す・外すと、次の質問から効きます。変えたことは操作履歴に残ります。'
            : // 役職の呼び名はその会社のもの（issue #480）
              `答えない領域は、会社のすべての拠点の質問に効きます。${wording.only(
                ROLE_REQUIREMENTS.restrictedTopics,
                '足す・直す・外すができるのは',
              )}`}
        </p>
      ) : null}

      {canEdit && plan ? (
        <div className="admin-restore-defaults">
          <Button
            variant="secondary"
            disabled={busy || plan.total === 0}
            onClick={() => setRestoring({ kind: 'all' })}
            aria-label="既定の領域と言葉を、すべて元に戻す"
            aria-describedby="restore-defaults-note"
          >
            既定の内容に戻す
          </Button>
          <p className="admin-restore-note" id="restore-defaults-note">
            {plan.total === 0
              ? '既定の領域と言葉はすべて入っています。'
              : restoreAllNote(plan.words, plan.removed)}
          </p>
        </div>
      ) : null}

      {result ? (
        <ResultBanner
          reveal
          tone={result.tone}
          message={result.message}
          {...(result.nextStep !== undefined ? { nextStep: result.nextStep } : {})}
        />
      ) : null}

      {error ? <ErrorBlock failure={error} onRetry={reload} /> : null}
      {!error && !data ? <LoadingBlock label="答えない領域を読み込んでいます…" /> : null}
      {data && data.topics.length === 0 ? (
        <EmptyBlock label="答えない領域は決められていません。" />
      ) : null}

      {data && data.topics.length > 0 ? (
        <div className="admin-table-top">
          <DataTable label="答えない領域の一覧">
            <thead>
              <tr>
                <th scope="col">領域</th>
                {/* 「直す」「外す」は領域のすぐ隣に置く。右端に置くと、スマホの幅では横にずらした先に隠れる（#475 W-14） */}
                {canEdit ? (
                  <th scope="col">
                    <span className="admin-visually-hidden">操作</span>
                  </th>
                ) : null}
                <th scope="col">この言葉が入っていたら</th>
                <th scope="col">お返しする案内</th>
                <th scope="col">連絡先</th>
                {/* 1280px の幅で最後の列が横送りの先に隠れないよう、補足を2行目に出して列を細くする（#481 のレビュー） */}
                <th scope="col">
                  件数だけ数える<small>（中身は残さない）</small>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.topics.map((topic) => (
                <tr key={topic.key}>
                  <td className="ui-cell-soft">{topic.label}</td>
                  {canEdit ? (
                    <td>
                      <div className="admin-row-actions">
                        <Button
                          variant="secondary"
                          compact
                          disabled={busy}
                          onClick={() => startEditing(topic)}
                          aria-label={`「${topic.label}」を直す`}
                        >
                          直す
                        </Button>
                        <Button
                          variant="quiet"
                          compact
                          disabled={busy}
                          onClick={() => setRemoving(topic)}
                          aria-label={`「${topic.label}」を外す`}
                        >
                          外す
                        </Button>
                        {topic.missing_default_keywords !== null ? (
                          <Button
                            variant="quiet"
                            compact
                            disabled={busy || topic.missing_default_keywords.length === 0}
                            onClick={() => setRestoring({ kind: 'one', topic })}
                            aria-label={`「${topic.label}」を既定の内容に戻す`}
                            {...(topic.missing_default_keywords.length === 0
                              ? { 'aria-describedby': `restore-note-${topic.key}` }
                              : {})}
                          >
                            既定の内容に戻す
                          </Button>
                        ) : null}
                      </div>
                      {topic.missing_default_keywords?.length === 0 ? (
                        <p className="admin-restore-note" id={`restore-note-${topic.key}`}>
                          既定の言葉はすべて入っています。
                        </p>
                      ) : null}
                    </td>
                  ) : null}
                  <td className="ui-cell-wrap">{topic.keywords.join('・')}</td>
                  <td className="ui-cell-wrap">{topic.guidance}</td>
                  <td className="ui-cell-soft">
                    {topic.contact === '' ? '（決まっていません）' : topic.contact}
                  </td>
                  <td>{topic.sensitive ? 'はい' : 'いいえ'}</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </div>
      ) : null}

      {canEdit && editing ? (
        <div ref={editArea}>
          <FormCard
            title={`「${editing.label}」を直す`}
            submitLabel="保存する"
            submitBusyLabel="保存しています…"
            busy={busy}
            onSubmit={() => void saveEdit()}
            onCancel={() => setEditing(null)}
          >
            <TopicFields idPrefix="topic-edit" draft={editDraft} onChange={setEditDraft} />
          </FormCard>
        </div>
      ) : null}

      {canEdit ? (
        <FormCard
          title="領域を足す"
          description="この言葉が入った質問には、AI先輩は答えず、ここに書いた案内をお返しします。"
          submitLabel="足す"
          submitBusyLabel="足しています…"
          busy={busy}
          submitVariant={editing ? 'secondary' : 'primary'}
          onSubmit={() => void addTopic()}
        >
          <TopicFields idPrefix="topic-add" draft={adding} onChange={setAdding} />
        </FormCard>
      ) : null}

      <ConfirmDialog
        open={removing !== null}
        title="この領域を外します"
        message={removing ? removalMessage(removing, data?.topics.length ?? 0) : ''}
        reversibility="同じ内容を足し直せば元に戻せます。拾う言葉は操作履歴に一部しか残らないため、多いときは外す前に「直す」の欄から写しておいてください。"
        reversible
        confirmLabel="外す"
        busy={busy}
        onConfirm={() => {
          if (removing) void removeTopic(removing);
        }}
        onCancel={() => setRemoving(null)}
      />

      <ConfirmDialog
        open={restoring !== null && data !== null}
        title="既定の内容に戻します"
        message={restoring && data ? <RestoreMessage target={restoring} data={data} /> : ''}
        reversibility={restoring && data ? restoreReversibility(restoring, data) : ''}
        reversible
        tone="primary"
        confirmLabel="戻す"
        busy={busy}
        onConfirm={() => {
          if (restoring) void restoreDefaults(restoring);
        }}
        onCancel={() => setRestoring(null)}
      />
    </article>
  );
}

/** 画面全体の「既定の内容に戻す」で戻るもの。 */
function restorePlan(data: AiRestrictedTopicsResponse): {
  words: number;
  removed: AiRemovedDefaultTopic[];
  total: number;
} {
  const words = data.topics.reduce((n, t) => n + (t.missing_default_keywords?.length ?? 0), 0);
  const removed = data.removed_defaults;
  return { words, removed, total: words + removed.length };
}

/** 画面全体の「既定の内容に戻す」の横に出す一言（押すと何が起きるか）。 */
function restoreAllNote(words: number, removed: AiRemovedDefaultTopic[]): string {
  const parts: string[] = [];
  if (words > 0) {
    parts.push(
      `既定の言葉のうち、いまの一覧に無い${words}語を足し戻せます（足した言葉は消えません）。`,
    );
  }
  if (removed.length > 0) {
    parts.push(
      `外した既定の領域（${removed.map((r) => `「${r.label}」`).join('')}）を作り直せます。`,
    );
  }
  return parts.join('');
}

/** 戻る言葉を見せる（多いときは先頭の数語と「ほか◯語」）。 */
function previewWords(words: readonly string[]): string {
  const head = words
    .slice(0, PREVIEW_WORDS)
    .map((w) => `「${w}」`)
    .join('');
  const rest = words.length - PREVIEW_WORDS;
  return rest > 0 ? `${head}ほか${rest}語` : head;
}

/** 言葉を戻す領域ごとの1文。 */
function wordsLine(topic: AiRestrictedTopic): string {
  const words = topic.missing_default_keywords ?? [];
  return `「${topic.label}」に、次の${words.length}語を戻します：${previewWords(words)}`;
}

/** 外してある既定の領域ごとの1文。 */
function recreateLine(removed: AiRemovedDefaultTopic): string {
  return `外してある「${removed.label}」を、既定の内容（拾う言葉${removed.keywords.length}語）で作り直します。外す前に足していた言葉は戻りません。`;
}

/**
 * 「既定の内容に戻す」の確認の窓の中身（決定22）。何が戻るかを具体の言葉と数で書き、
 * 変えないもの（足した言葉・名前・案内・連絡先・「件数だけ数える」）もはっきり書く。
 */
function RestoreMessage({
  target,
  data,
}: {
  target: RestoreTarget;
  data: AiRestrictedTopicsResponse;
}): ReactNode {
  const topics =
    target.kind === 'one'
      ? [target.topic]
      : data.topics.filter((t) => (t.missing_default_keywords?.length ?? 0) > 0);
  const removed = target.kind === 'one' ? [] : data.removed_defaults;
  const lines = [...topics.map(wordsLine), ...removed.map(recreateLine)];
  // 既定では「件数だけ数える」なのに外している領域は、戻しても外れたまま（直したのはお客様の選択のため）。
  // 言葉を戻せば AI先輩は答えなくなるが、相談の中身は記録に残り「よく聞かれた質問」に出うるので、
  // 状態だけでなく、それが何を意味するかも書く（決定22・#472 の趣旨＝繊細な相談の中身を見えなくする）
  const unchecked = topics.filter((t) => t.default_sensitive === true && !t.sensitive);
  // 確認の窓は中身を1つの段落に入れるので、行ごとに区切って並べる（段落や箇条書きは入れられない）
  const all = [
    ...lines.map((line) => (lines.length > 1 ? `・${line}` : line)),
    ...(topics.length === 0
      ? []
      : lines.length === 1
        ? ['いま入っている言葉と、領域の名前・案内・連絡先はそのままです。']
        : ['言葉を戻す領域では、いま入っている言葉と、領域の名前・案内・連絡先はそのままです。']),
    ...unchecked.map(
      (t) =>
        `「${t.label}」は、戻したあとも「件数だけ数える」が「いいえ」のままです。相談の中身は記録に残り、「利用状況」の「よく聞かれた質問」に出ることがあります。中身を残さないときは、「直す」で「件数だけ数える」を選んでください。`,
    ),
  ];
  return (
    <>
      {all.map((line) => (
        <span key={line} className="admin-dialog-line">
          {line}
        </span>
      ))}
    </>
  );
}

/** 確認の窓の「元に戻せるか」の1行（戻すものに合わせて書く）。 */
function restoreReversibility(target: RestoreTarget, data: AiRestrictedTopicsResponse): string {
  const words =
    target.kind === 'one' || data.topics.some((t) => (t.missing_default_keywords?.length ?? 0) > 0);
  const recreate = target.kind === 'all' && data.removed_defaults.length > 0;
  if (words && recreate)
    return '戻した言葉は「直す」から、作り直した領域は「外す」から、また外せます。';
  if (recreate) return '作り直した領域は「外す」から、また外せます。';
  return '戻した言葉は「直す」から、また外せます。';
}

/** 結果の帯の、領域ごとの1文。 */
function restoredSentence(restored: {
  label: string;
  keywords: string[];
  recreated: boolean;
}): string {
  return restored.recreated
    ? `「${restored.label}」を既定の内容で作り直しました（拾う言葉${restored.keywords.length}語）。`
    : `「${restored.label}」に既定の言葉を${restored.keywords.length}語戻しました。`;
}

/**
 * 外すときの確認の窓の言葉。何が変わるかを具体に書く（決定22）。
 *
 * 「件数だけ数える」にしている領域（既定の「給与・有給・労務・健康」など）を外すと、その相談の中身が
 * 「答えられなかった質問」に出て、管理する方が読めるようになる（#375 の守りが外れる）。
 * ふつうの領域と同じ言い方では伝わらないので、はっきり書き分ける。
 */
function removalMessage(topic: AiRestrictedTopic, total: number): string {
  const head = `「${topic.label}」を外すと、次の質問から、この領域の質問にも AI先輩が答えます。`;
  const exposed = topic.sensitive
    ? 'この領域は「件数だけ数える」にしているため、これまでは相談の中身を残していませんでした。外すと、答えが見つからない相談は、中身の文のまま「答えられなかった質問」に出て、管理する方が読めるようになります（お名前は出ません）。'
    : '答えが見つからないときは、「答えられなかった質問」に質問の文が出ます。';
  const last = total <= 1 ? '外すと、答えない領域は1つも無くなります。' : '';
  return head + exposed + last;
}

/** 足す・直すで共通の入力欄。 */
function TopicFields({
  idPrefix,
  draft,
  onChange,
}: {
  idPrefix: string;
  draft: Draft;
  onChange: (next: Draft) => void;
}): ReactNode {
  return (
    <>
      <label className="ui-field" htmlFor={`${idPrefix}-label`}>
        <span>領域の名前（{LABEL_MAX}文字まで。例：給与・有給・労務）</span>
        <input
          id={`${idPrefix}-label`}
          type="text"
          value={draft.label}
          onChange={(event) => onChange({ ...draft, label: event.target.value })}
        />
      </label>
      <label className="ui-field" htmlFor={`${idPrefix}-keywords`}>
        <span>
          この言葉が入っていたら（「、」か改行で区切って入れてください。例：給料、残業代）
        </span>
        <textarea
          id={`${idPrefix}-keywords`}
          rows={3}
          value={draft.keywords}
          onChange={(event) => onChange({ ...draft, keywords: event.target.value })}
        />
      </label>
      <label className="ui-field" htmlFor={`${idPrefix}-guidance`}>
        <span>お返しする案内（{GUIDANCE_MAX}文字まで）</span>
        <textarea
          id={`${idPrefix}-guidance`}
          rows={3}
          value={draft.guidance}
          onChange={(event) => onChange({ ...draft, guidance: event.target.value })}
        />
      </label>
      <label className="ui-field" htmlFor={`${idPrefix}-contact`}>
        <span>連絡先（空のままでも構いません。例：本部 総務部（内線12））</span>
        <input
          id={`${idPrefix}-contact`}
          type="text"
          value={draft.contact}
          onChange={(event) => onChange({ ...draft, contact: event.target.value })}
        />
      </label>
      <label className="ui-check" htmlFor={`${idPrefix}-sensitive`}>
        <input
          id={`${idPrefix}-sensitive`}
          type="checkbox"
          checked={draft.sensitive}
          onChange={(event) => onChange({ ...draft, sensitive: event.target.checked })}
        />
        <span>件数だけ数える（中身は残さない。給与・健康など、人に知られたくない相談の領域）</span>
      </label>
    </>
  );
}

function failure(head: string, caught: unknown, button: string): Banner {
  const reason = toMessage(caught);
  return {
    tone: 'error',
    message: withReason(head, reason),
    // 理由の文が次の一手まで言っているときは足さない（#286 L-6）
    ...(endsWithRequest(reason)
      ? {}
      : { nextStep: `入力の内容をご確認のうえ、もう一度${button}を押してください。` }),
  };
}

/** 失敗の理由を、運用される方が読める言葉にする。 */
function toMessage(caught: unknown): string {
  return caught instanceof ApiError ? caught.message : messageForStatus(0);
}
